import React, { useState, useCallback, forwardRef } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  // MouseSensor,
  TouchSensor,
  DragOverlay,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/**
 * @notice This is a stylable component for the draggable items
 * @dev Import this and style it as you want then pass it to DraggableBoard
 */
export const DraggableItem = forwardRef(
  (
    { id, withOpacity, isDragging, style, className, children, ...props },
    ref,
  ) => {
    const inlineStyles = {
      opacity: withOpacity ? "0.4" : "1",
      cursor: isDragging ? "grabbing" : "grab",
      transform: isDragging ? "scale(1.025)" : "scale(1)",
      ...style,
    };

    return (
      <div ref={ref} style={inlineStyles} className={className} {...props}>
        {children}
      </div>
    );
  },
);

/**
 *
 * @notice wrapper component for the sortable item
 */
const SortableItem = (props) => {
  const { children } = props;
  const {
    isDragging,
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: props.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
  };

  return children({
    ref: setNodeRef,
    style,
    withOpacity: isDragging,
    ...props,
    ...attributes,
    ...listeners,
  });
};

/**
 *
 * @dev this draggable board is only design to support with sortable item and sortable context
 * @param {Object} props - items, onDragEnd, Components
 * @param {Object[]} props.items - Array of items (item._id is required)
 * @param {Function} props.onDragEnd - Callback function to handle drag end, receive { active, over }
 * @param {Object} props.Components - Object of components to be used in the draggable board
 * @param {Function} props.Components.DroppableParent - Function to render the parent component
 * @param {Function} props.Components.DraggableItem - Function to render the draggable item
 * @returns
 */
const DraggableBoard = ({ items, onDragEnd, Components }) => {
  const { DroppableParent, DraggableItem } = Components;
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor),
  );

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={({ active, over }) => {
        onDragEnd({ active, over });
        setActiveId(null);
      }}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={items} strategy={rectSortingStrategy}>
        {DroppableParent({
          items,
          children: ({ items }) => {
            return items.map((item, index) => (
              <SortableItem key={item._id} id={item._id}>
                {(props) => {
                  return DraggableItem({
                    ...props, // this will include ref, style, withOpacity, etc.
                    items,
                    item,
                    index,
                  });
                }}
              </SortableItem>
            ));
          },
        })}
      </SortableContext>
      <DragOverlay adjustScale style={{ transformOrigin: "0 0 " }}>
        {activeId
          ? DraggableItem({
              item: items.find((item) => item._id === activeId),
              id: activeId,
              isDragging: true,
            })
          : null}
      </DragOverlay>
    </DndContext>
  );
};

export default DraggableBoard;
